import type { Dispatch, SetStateAction } from 'react';
import { Checkbox, ConfigProvider } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import type { PageType, TableHeader, VisibleCategories } from '@/types';
import { TotalImpressionsTooltipEcho } from '@/components/tooltips/ImpressionsTooltipsEcho';
import {
  areAllCategoriesVisible,
  areAnyCategoriesVisible,
  toggleAllCategories,
} from './CheckboxUtils';

interface TableColumnsProps {
  popoverRef: React.RefObject<HTMLDivElement>;
  visibleCategories: VisibleCategories;
  setVisibleCategories: Dispatch<SetStateAction<VisibleCategories>>;
  pageType: PageType;
  token: GlobalToken;
  isMobile: boolean;
}

/**
 * This is the header data for the campaign types table.
 * @param popoverRef - The ref for the impressions tooltip.
 * @param visibleCategories - The visible campaign categories.
 * @param setVisibleCategories - The function to set the visible campaign categories.
 * @param pageType - The type of page. 'earnings' or 'payouts'
 * @param token - The Ant Design theme token.
 * @param isMobile - Whether the viewport is mobile-sized.
 * @returns The header data.
 */
const TableColumnsEcho = ({
  popoverRef,
  visibleCategories,
  setVisibleCategories,
  pageType,
  token,
  isMobile,
}: TableColumnsProps): TableHeader[] => {
  const columns: TableHeader[] = [
    {
      id: 'campaignType',
      label: 'Campaign type',
      sortable: true,
      style: {
        minWidth: isMobile ? '80dvw' : 500,
        ...(pageType === 'payouts' && { paddingInlineStart: token.paddingLG }),
      },
    },
    {
      id: 'impressions',
      label: <TotalImpressionsTooltipEcho popoverRef={popoverRef} />,
      sortable: true,
      styleOptions: { align: 'right' },
    },
    {
      id: 'earnings',
      label: 'Estimated earnings',
      sortable: true,
      style: { minWidth: 200 },
      styleOptions: { align: 'right' },
    },
    {
      id: 'percentage',
      label: '% of Total',
      sortable: true,
      style: { minWidth: 100, paddingInlineEnd: token.paddingLG },
      styleOptions: { align: 'right' },
    },
  ];

  if (pageType !== 'payouts') {
    columns.unshift({
      id: 'checkbox',
      label: (
        <ConfigProvider
          theme={{ token: { colorPrimary: token.colorPrimary, colorPrimaryHover: token.colorPrimary } }}
        >
          <Checkbox
            name="select-all-checkbox"
            checked={areAllCategoriesVisible(visibleCategories)}
            indeterminate={
              areAnyCategoriesVisible(visibleCategories) &&
              !areAllCategoriesVisible(visibleCategories)
            }
            onChange={() => {
              const shouldShowAll = !areAllCategoriesVisible(visibleCategories);
              setVisibleCategories(toggleAllCategories(visibleCategories, shouldShowAll));
            }}
          />
        </ConfigProvider>
      ),
      sortable: false,
      style: {
        width: 30,
        paddingInlineStart: token.paddingLG,
        paddingInlineEnd: token.paddingSM,
        paddingBlock: token.paddingSM,
      },
    });
  }

  return columns;
};

export default TableColumnsEcho;
